import { useEffect, useState } from 'react';

export default function useSessionStorage<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    try {
      const item = window.sessionStorage.getItem(key);

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(item ? (JSON.parse(item) ?? defaultValue) : defaultValue);

      if (!item) {
        window.sessionStorage.setItem(key, JSON.stringify(defaultValue));
      }
    } catch {
      // Storage may be blocked or contain invalid JSON; keep the in-memory value.
    }

    function handler(e: StorageEvent) {
      if (e.key !== key) return;

      try {
        const item = window.sessionStorage.getItem(key);
        setValue(item ? (JSON.parse(item) ?? defaultValue) : defaultValue);
      } catch {
        // A failed storage read must not interrupt the page or reset local state.
      }
    }

    window.addEventListener('storage', handler);

    return () => {
      window.removeEventListener('storage', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValueWrap = (value: T) => {
    setValue(value);

    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      window.dispatchEvent(new StorageEvent('storage', { key }));
    } catch {
      // State updates still work when persistence is unavailable.
    }
  };

  return [value, setValueWrap];
}
